<script setup lang="ts">
import FilterDialog from "@/components/FilterBuilder/FilterDialog.vue";
import TableHeader from "@/components/DataTable/TableHeader.vue";
import TableHeaderItem from "@/components/DataTable/TableHeaderItem.vue";
import TableHeaderPaginationGroup from "@/components/DataTable/TableHeaderPaginationGroup.vue";
import ListSearchBox from "@/components/DataTable/ListSearchBox.vue";
import FilterChips from "@/components/DataTable/FilterChips.vue";
import ListDisplayMenu, { type DisplayLayoutOption } from "@/components/DataTable/ListDisplayMenu.vue";
import TableGridIcon from "@/components/icons/TableGridIcon.vue";
import { usePersistentTable } from "@/composables/usePersistentTable";
import { cardFixedColumnIdsFor, fixedCardHeaderComponentFor } from "@/composables/searchInspectorRegistry";
import { getSearchTableCustomColumns } from "@/composables/searchTableCustomColumns";
import { computed, markRaw, ref } from "vue";
import { useMediaQuery } from "@vueuse/core";
import { storeToRefs } from "pinia";
import { useEntityStore } from "@/stores/entityStore";
import { IdentificationIcon, MapIcon, Squares2X2Icon } from "@heroicons/vue/24/outline";
import type { IScope } from "@/types/filterBuilderTypes";
import { searchToolbarStatusLayout } from "@/composables/toolbarFitState";
import type { SyncPillForm } from "@/utils/syncStatus";
import { useToolbarFit } from "@/composables/useToolbarFit";
import { countFilterRulesExceptListSearch, filterRuleCountLabel } from "@/components/FilterBuilder/filterRuleCountLabel";

/**
 * The list toolbar. From left to right: the search box, "Filter", a gap,
 * "Display ▾", the page's actions (Export), then the live dot and the pager.
 * It is the same in Table, in Cards and on the map. Switching the layout
 * changes the list, not the toolbar. A control that does not apply to a
 * layout is left out of the Display menu, never out of the toolbar.
 *
 * Spec: docs/specs/macro-style-layout.md, "Lists".
 */

const MAP_SEARCH_ENTITY_IDS = ["Account", "Location", "LocationZone"] as const;

const props = defineProps<{
  totalRecords?: number;
  /**
   * The list shows the Sync pill in the status slot (see showsSyncPill). The pill
   * then sits inside the measured row on desktop too, see searchToolbarStatusLayout.
   */
  hasSyncPill?: boolean;
  /** 1-based current page for the cursor-based pager label. */
  currentPage?: number;
  /** Cursor navigation gates + in-flight flag, forwarded to the pager. */
  hasNext?: boolean;
  hasPrev?: boolean;
  loading?: boolean;
}>();

defineEmits<{
  navigate: [direction: "first" | "prev" | "next" | "last"];
}>();

defineSlots<{
  /**
   * The status cluster (Sync pill and live-polling indicator). Placed by
   * breakpoint and fit state, see searchToolbarStatusLayout. `form` follows the
   * fit state: "Sync 7 · 5" while the toolbar shows words, else the icon.
   */
  status?: (props: { form: SyncPillForm }) => unknown;
  /**
   * Page-owned actions after the Display button. `fit` is the room the
   * toolbar has left for them: `full` while labels still fit, else `icons`.
   */
  actions?: (props: { fit: "full" | "icons" }) => unknown;
}>();

const { selectedEntityId } = storeToRefs(useEntityStore());

const {
  filter,
  setFilter,
  storeKey,
  searchViewMode,
  setSearchViewMode,
  tableLayout,
  setTableLayout,
  absoluteOrderedColumns,
  visiblePinnedColumns,
  visibleNonPinnedColumns,
  cardVisibleColumns,
  groupBy,
  setGroupBy,
} = usePersistentTable();

const canUseMapView = computed(() =>
  MAP_SEARCH_ENTITY_IDS.includes(
    selectedEntityId.value as (typeof MAP_SEARCH_ENTITY_IDS)[number],
  ),
);

/**
 * The layout spans two per-entity store fields: Map lives in searchViewMode
 * ('list' | 'map'), Table vs Cards lives in tableLayout ('rows' | 'cards')
 * while searchViewMode is 'list'.
 */
const hasFixedCard = computed(
  () => fixedCardHeaderComponentFor(selectedEntityId.value) !== null,
);

type SearchView = "table" | "cards" | "fixed-cards" | "map";

const effectiveSearchView = computed<SearchView>(() => {
  if (canUseMapView.value && searchViewMode.value === "map") {
    return "map";
  }
  if (tableLayout.value === "fixed-cards") {
    // A persisted fixed-cards preference on an entity without a fixed card
    // (e.g. after switching entities) falls back to plain cards.
    return hasFixedCard.value ? "fixed-cards" : "cards";
  }
  return tableLayout.value === "cards" ? "cards" : "table";
});

/** The Layout choices. The test ids are the ones the old view menu used. */
const layoutOptions = computed<DisplayLayoutOption[]>(() => [
  { value: "table", label: "Table", icon: markRaw(TableGridIcon), testId: "search-view-list" },
  { value: "cards", label: "Cards", icon: markRaw(Squares2X2Icon), testId: "search-view-cards" },
  ...(hasFixedCard.value
    ? [{ value: "fixed-cards", label: "Fixed Card", icon: markRaw(IdentificationIcon), testId: "search-view-fixed-cards" }]
    : []),
  ...(canUseMapView.value
    ? [{ value: "map", label: "Map", icon: markRaw(MapIcon), testId: "search-view-map" }]
    : []),
]);

function onLayoutChange(value: string) {
  if (value === "map") {
    setSearchViewMode("map");
    return;
  }
  if (value === "table" || value === "cards" || value === "fixed-cards") {
    setSearchViewMode("list");
    setTableLayout(value === "table" ? "rows" : value);
  }
}

/** Group by applies to the table and to cards, which draw group headers too. Not to the map. */
const groupByOptions = computed(() =>
  effectiveSearchView.value === "map"
    ? null
    : absoluteOrderedColumns.value
        .filter((column) => column.isSortable !== false && column.dataType !== "display")
        .map((column) => ({ value: column.field, label: column.header })),
);

/**
 * "Columns…" opens the Columns page of the Display menu. Its words say what
 * the page edits:
 *
 * - In the table it reads "Columns…".
 * - In Cards it reads "Card fields…", because the page picks the fields on
 *   the cards, not the table's columns.
 * - On the map it reads "Map fields…".
 *
 * The Fixed Card layout draws the entity's fixed card only, with no columns,
 * so it has no link.
 */
const columnsLabel = computed(() => {
  if (effectiveSearchView.value === "fixed-cards") return null;
  if (effectiveSearchView.value === "map") return "Map fields…";
  return effectiveSearchView.value === "cards" ? "Card fields…" : "Columns…";
});

/**
 * How many columns the table shows, or how many fields each card shows, for
 * "6 shown". A card's count includes the fields every card always draws, such
 * as its ID and Name, because the person sees those on the card too. They are
 * the ones the page lists under "Always on cards". The map shows no count.
 */
const columnsShownCount = computed(() => {
  if (effectiveSearchView.value === "table") {
    return [...visiblePinnedColumns.value, ...visibleNonPinnedColumns.value].filter((column) => !column.cardOnly).length;
  }
  if (effectiveSearchView.value === "cards") {
    const fixedColumnIds = new Set(cardFixedColumnIdsFor(storeKey));
    const fixedFieldCount = absoluteOrderedColumns.value.filter((column) => fixedColumnIds.has(column.id)).length;
    const chosenFieldCount = cardVisibleColumns.value.filter((column) => !fixedColumnIds.has(column.id)).length;
    return fixedFieldCount + chosenFieldCount;
  }
  return null;
});

/** The map's Columns page picks the fields of its pins' cards. */
const columnsMapMode = computed(() => effectiveSearchView.value === "map");

/** Custom columns, such as Play and Currently Playing, can be shown or hidden too. */
const columnsAdditionalColumns = computed(() => getSearchTableCustomColumns(selectedEntityId.value));

// One status cluster, rendered in the group searchToolbarStatusLayout picks
// from the breakpoint, the fit state and whether the list has the Sync pill.
// The media query gates a `v-if` (not CSS), so only one copy is ever in the DOM.
const isDesktop = useMediaQuery("(min-width: 768px)");

// Room-based fit, three states (see toolbarFitState.ts for the ladder):
// 'full' (Filter, Display and Export labelled) → 'icons' (the same buttons,
// icon-only) → 'collapsed' (the search box may give up width as well).
// Nothing folds into a menu: three icon buttons fit a phone. Room decides, not
// viewport breakpoints. The left group is `w-full`, so it is both the room and
// the content: useToolbarFit measures its own box for both halves.
const leftGroup = ref<HTMLElement | null>(null);
/** The status cluster while it sits in the measured row. The pill's counts can widen it. */
const statusInRow = ref<HTMLElement | null>(null);

// The cached per-state widths are only valid for the label text they were
// measured with. When a label source changes (the entity, or the filter's
// count badge, "2" → "12"), stale caches could promote into a state that no
// longer fits.
const filterRuleCountLabelText = computed(() =>
  filterRuleCountLabel(filter.value),
);
const toolbarLabelSignature = computed(() =>
  // NUL-escape separator, so two different tuples cannot collide into one signature.
  [selectedEntityId.value, filterRuleCountLabelText.value].join("\u0000"),
);

const { fitState: toolbarFitState } = useToolbarFit({
  roomRef: leftGroup,
  labelSignature: toolbarLabelSignature,
  observedControlRefs: [statusInRow],
});

/**
 * How many rules the Filter button's badge shows. The list search box's rule
 * is left out, because the search box already shows it.
 */
const filterRuleCount = computed(() =>
  filter.value ? countFilterRulesExceptListSearch(filter.value) : 0,
);

const statusLayout = computed(() =>
  searchToolbarStatusLayout(isDesktop.value, toolbarFitState.value, props.hasSyncPill),
);

function applyFilterHandler(parsedQuery: IScope) {
  setFilter(parsedQuery);
}
</script>

<template>
  <!--
    `pb-0`: the search page's results surface floats on a tinted ground and
    supplies its own 12px gap, so the toolbar drops its bottom padding rather
    than stacking a second one — the controls sit just above the surface.
  -->
  <div class="flex flex-col gap-2 pb-0">
  <TableHeader left-full-on-mobile left-grow class="pb-0">
    <template #left>
      <!-- Measured row: `toolbarFitState` walks the full → icons → collapsed
           ladder when this box can't fit its controls (see useToolbarFit /
           toolbarFitState.ts). -->
      <!--
        The control cluster is filled with the content surface's own colour
        HERE rather than in the shared `toolbar` button variant: this is the
        only toolbar standing on a tinted ground, where a `bg-muted/50` chip
        dissolves. Every other page's toolbar is still `bg-background`, and
        there the muted chip is exactly what reads — changing the variant would
        have turned those buttons white-on-white.

        `[data-slot$=trigger]` catches the popover / dropdown / select triggers,
        which override `data-slot` and so are not `[data-slot=button]`. Scoped
        to this cluster, so the pager's ghost buttons stay ghost. Hover is
        restated in the same scope so it outranks the variant's.
      -->
      <div
        ref="leftGroup"
        class="flex min-w-0 w-full items-center gap-1
               [&_[data-slot=button]]:bg-background [&_[data-slot$=trigger]]:bg-background
               [&_[data-slot=button]:hover]:bg-muted/60 [&_[data-slot$=trigger]:hover]:bg-muted/60
               dark:[&_[data-slot=button]]:bg-background dark:[&_[data-slot$=trigger]]:bg-background
               dark:[&_[data-slot=button]:hover]:bg-muted/40 dark:[&_[data-slot$=trigger]:hover]:bg-muted/40"
      >
        <!-- The rounded search box narrows the list by the entity's name column.
             It keeps its width while the toolbar can still drop labels. Once
             only icons are left, it is the one thing that can give up room, so
             it shrinks to keep the buttons after it on screen. Shrinking
             earlier would stop the ladder from ever dropping labels, because
             the row would always fit. -->
        <TableHeaderItem
          class="w-64 lg:w-80"
          :class="toolbarFitState === 'collapsed' ? 'min-w-32 flex-[0_1_auto]' : 'flex-none'"
          data-testid="toolbar-search-slot"
        >
          <ListSearchBox />
        </TableHeaderItem>
        <!-- Filter sits right after the search box, because both narrow the
             list. It is labelled while there is room, and keeps its count
             badge at every width. It is the one way to open the filter
             builder. -->
        <TableHeaderItem class="relative flex-none">
          <FilterDialog
            :key="storeKey"
            :trigger-label-mode="toolbarFitState === 'full' ? 'word' : 'iconOnly'"
            fullscreen
            @applyFilter="applyFilterHandler"
          />
          <span
            v-if="filterRuleCount > 0"
            data-testid="filter-count-badge"
            class="pointer-events-none absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-foreground px-1 text-[10px] font-bold text-background"
            :aria-label="`${filterRuleCount} filter rules`"
          >{{ filterRuleCount }}</span>
        </TableHeaderItem>

        <div class="flex-1" data-testid="toolbar-spacer" />

        <!-- Display: Layout, Group by and Columns, in one menu. -->
        <TableHeaderItem class="flex-none">
          <ListDisplayMenu
            :layout-options="layoutOptions"
            :layout="effectiveSearchView"
            :group-by-options="groupByOptions"
            :group-by="groupBy"
            :columns-label="columnsLabel"
            :columns-shown-count="columnsShownCount"
            :show-label="toolbarFitState === 'full'"
            columns-test-id="column-settings-button"
            :columns-map-mode="columnsMapMode"
            :columns-additional-columns="columnsAdditionalColumns"
            :page-memory-key="selectedEntityId"
            @update:layout="onLayoutChange"
            @update:group-by="setGroupBy"
          />
        </TableHeaderItem>

        <!-- Page-supplied actions (the list page's Export button). The slot is
             told whether the toolbar still has room for labels; below that it
             gets icons. -->
        <TableHeaderItem v-if="$slots.actions" class="flex-none">
          <slot
            name="actions"
            :fit="toolbarFitState === 'full' ? 'full' : 'icons'"
          />
        </TableHeaderItem>

        <!-- Status cluster inside the measured row: below md, and on the Players
             list at every width, while the toolbar is full or icons. It trails the
             toolbar buttons and takes part in the fit ladder. Only one instance is
             ever in the DOM (no duplicate testid). -->
        <TableHeaderItem v-if="$slots.status && statusLayout.inLeftGroup" class="flex-none">
          <div ref="statusInRow" class="flex items-center" data-testid="toolbar-status-left">
            <slot name="status" :form="statusLayout.form" />
          </div>
        </TableHeaderItem>
      </div>
    </template>

    <template #right>
      <!-- Status cluster beside the pager: at md+ on lists without the Sync pill,
           and on every list once the measured row has collapsed (below md the
           right group is then its own full-width row). -->
      <!-- flex-none and start-aligned: a TableHeaderItem grows and centres by default,
           which on the wrapped small-screen row indented the pill. This keeps it flush
           with the entity selector's left edge on the row above. -->
      <TableHeaderItem
        v-if="$slots.status && !statusLayout.inLeftGroup"
        class="flex-none shrink-0 max-md:items-start max-md:justify-start"
        data-testid="toolbar-status-right"
      >
        <slot name="status" :form="statusLayout.form" />
      </TableHeaderItem>
      <!-- Fills the width on the wrapped small-screen row (the pager group
           spreads edge to edge); compact content-width cluster at md+. -->
      <TableHeaderItem class="md:min-w-max md:shrink-0">
        <TableHeaderPaginationGroup
          cursor-mode
          :total-records="totalRecords"
          :current-page="currentPage"
          :has-next="hasNext"
          :has-prev="hasPrev"
          :loading="loading"
          @navigate="$emit('navigate', $event)"
        />
      </TableHeaderItem>
    </template>
  </TableHeader>
  <FilterChips />
  </div>
</template>
